import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";

import { Layout, notification, Spin, Modal } from '@lockerpm/design';

import {
  ExclamationCircleOutlined
} from '@ant-design/icons';

import './assets/css/index.scss';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import 'react-phone-number-input/style.css'

import AdminLayout from './layouts';
import OtherLayout from './layouts/other';

import authServices from "./services/auth";
import systemServices from "./secrets-web/src/services/system";
import initJslib from './secrets-web/src/core-js/index';

import global from './config/global';
import storeActions from './store/actions';
import pages from "./pages"
import { navigatePage } from "./secrets-web/src/utils/navigate";
import { isEmpty, getRouterByLocation, updateLocale } from "./secrets-web/src/utils/common";

function App() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const location = useLocation();

  notification.config({ placement: 'bottomLeft', duration: 3 })
  global.notification = (type, message, description) => { notification[type]({ message, description }) }
  global.navigate = (name, params = {}, query = {}) => navigatePage(navigate, dispatch, name, params, query);
  global.pushSuccess = (message) => {
    global.notification(
      'success',
      t('notification.success.title'),
      message
    );
  }
  global.pushError = (error) => {
    const message = error?.response?.data?.message;
    global.notification(
      'error',
      t('notification.error.title'),
      message || t('notification.error.message.default')
    )
  }

  global.confirm = (handleOK = () => { }, options = {}) => Modal.confirm({
    title: options.title || t('common.confirm'),
    icon: <ExclamationCircleOutlined />,
    content: options.content || t('common.delete_question'),
    okText: options.okText || t('button.delete'),
    cancelText: t('button.cancel'),
    okButtonProps: options.okButtonProps || { danger: true },
    onOk: handleOK
  })

  const workspaces = useSelector((state) => state.workspace.workspaces);
  const projects = useSelector((state) => state.project.projects);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const selectedWorkspace = useSelector((state) => state.workspace.selectedWorkspace);
  const currentPage = useSelector((state) => state.system.currentPage);
  const isLoading = useSelector((state) => state.system.isLoading);

  useEffect(() => {
    const currentPage = getRouterByLocation(location)
    if (currentPage && !currentPage.not_auth) {
      authServices.redirect_login()
    }
  }, [])

  useEffect(() => {
    if (isEmpty(userInfo)) {
      const locale = systemServices.get_language();
      updateLocale(locale)
    } else {
      const locale = userInfo.customer_language
      updateLocale(locale)
    }
  }, [userInfo])

  useEffect(() => {
    const router = getRouterByLocation(location);
    dispatch(storeActions.updateCurrentPage(router));
  }, [location])

  useEffect(() => {
    if (selectedWorkspace && userInfo) {
      const ws = userInfo?.organizations?.find(o => o.organization_id === selectedWorkspace.id)
      dispatch(storeActions.updateWsRole(ws?.role || null))
    }
  }, [userInfo, selectedWorkspace])

  useEffect(() => {
    beforeRedirect();
  }, [location, userInfo, workspaces, projects])

  const beforeRedirect = async () => {
    const currentPage = getRouterByLocation(location)
    const isLocked = await checkLock()
    if (!userInfo) {
      if (!currentPage) {
        global.navigate('404');
        return;
      }
      if (currentPage.name === 'SPLASH') {
        authServices.redirect_login()
      }
    } else if (userInfo && !isLocked) {
      if (!currentPage) {
        global.navigate('ADMIN_ERROR');
        return;
      }
      if ((isEmpty(userInfo)) && !currentPage.not_auth) {
        await authServices.logout()
        return;
      }
      if (!isEmpty(userInfo) && !currentPage.not_auth) {
        if (currentPage.name === 'SPLASH') {
          dispatch(storeActions.updateSelectedWorkspace(workspaces[0]));
          global.navigate("PROJECTS", { workspace_id: workspaces[0].id });
          return;
        }
        if (currentPage.params?.workspace_id) {
          const currentSelectedWorkspace = workspaces.find((w) => w.id == currentPage.params?.workspace_id);
          if (!currentSelectedWorkspace) {
            global.navigate('404');
            return;
          }
          dispatch(storeActions.updateSelectedWorkspace(currentSelectedWorkspace));
          if (currentPage.params?.project_id) {
            const currentSelectedProject = projects.find((w) => w.id == currentPage.params?.project_id);
            if (currentSelectedProject) {
              dispatch(storeActions.updateSelectedProject(currentSelectedProject));
            } else {
              global.navigate('404');
            }
          } else {
            dispatch(storeActions.updateSelectedProject(null));
          }
          return;
        }
      }
    }
  }

  const checkLock = async () => {
    if (!global.jsCore) {
      global.jsCore = await initJslib();
    }
    return await global.jsCore?.vaultTimeoutService.isLocked()
  }

  return (
    <Layout>
      {
        currentPage?.type === 'admin' && <Spin spinning={isLoading}>
          <AdminLayout
            routers={global.routers.ADMIN_ROUTERS}
            pages={pages}
          />
        </Spin>
      }
      {
        currentPage?.type === 'auth' && <OtherLayout
          routers={global.routers.AUTH_ROUTERS}
          pages={pages}
        />
      }
      {
        currentPage?.type === 'error' && <OtherLayout
          routers={global.routers.ERROR_ROUTERS}
          pages={pages}
        />
      }
    </Layout>
  );
}

export default App;
